import React, { useContext, useState, useRef } from "react";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import { EtatGlobalContext } from "../EtatGlobal.js";
import "./Lead.css";
import { httpClient, authFetch } from "../../HttpClient";
// import { MdDelete } from "react-icons/md";
import { MdDownload } from "react-icons/md";

import { dialog } from "../../components/ui/Dialog";
import { showStatusToast } from "../../components/ui/Toast.js";
import LeadDocumentsList from "../../components/LeadDocumentsList";
import {
  X,
  Building2,
  MoreVertical,
  Users,
  User,
  Calendar,
  FileText,
  Briefcase,
  MapPin,
} from "lucide-react";

import { decouperSujetLM, tronquer } from "./lmSujet";

// Les deux circuits d'envoi d'une LM (direct via DetailsSign.js, ou soumission
// pour validation via lmSignaturePositions.js côté backend) nomment le
// signataire comptable différemment ("Mrs Samuel" vs "Samuel HOARAU") — seul
// l'email reste stable dans les deux cas, on matche donc dessus.
const COMPTABLE_SIGNATAIRES = {
  "samuel.hoarau@alfacomptabilite.com": "Samuel HOARAU",
  "anthony.hoarau@alfacomptabilite.com": "Anthony HOARAU",
};

const getSignataireComptable = (signers) =>
  (signers || []).find((s) =>
    Object.prototype.hasOwnProperty.call(
      COMPTABLE_SIGNATAIRES,
      (s.email || "").toLowerCase()
    )
  );

function PropLM({
  searchTerm,
  columnId = "prop_lm",
  loadingDocIds,
  docOverrides,
}) {
  const traduireStatut = (statut) => {
    switch (statut) {
      case "sent":
        return "Envoyée";
      case "delivered":
        return "Ouverte";
      case "completed":
        return "Terminée";
      case "declined":
        return "Refusée";
      case "voided":
        return "Annulée";
      case "signed":
        return "Signée";
      case "expired":
        return "Expirée";
      default:
        return statut; // ou "Inconnu"
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "sent":
        return "bg-yellow-50 text-yellow-600 border-yellow-200";

      case "delivered":
        return "bg-blue-50 text-blue-600 border-blue-200";

      case "voided":
        return "bg-red-50 text-red-600 border-red-200";

      case "declined":
        return "bg-red-50 text-red-600 border-red-200";

      case "signed":
        return "bg-green-50 text-green-600 border-green-200";

      case "completed":
        return "bg-gray-100 text-gray-600 border-gray-200";

      case "expired":
        return "bg-yellow-50 text-yellow-600 border-yellow-200";

      default:
        return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };
  const { documents, setDocuments, loadingDocumentsLM: isLoading, fetchDocumentsLM, utilisateur } =
    useContext(EtatGlobalContext);
  const [downloading, setDownloading] = useState({});
  const [processing, setProcessing] = useState({});

  const overlayRef = useRef(null);
  const inoverlayRef = useRef(null);

  const [openDetails, setOpenDetails] = React.useState({});
  const [showMenu, setShowMenu] = useState(false);
  const [activeTab, setActiveTab] = useState("general");

  // États à ajouter dans ton composant :
  const [showOverlay, setShowOverlay] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Rappels manuels envoyés pendant que ce document est ouvert, affichés
  // immédiatement dans la Timeline sans attendre le refetch de `documents`
  // (selectedDocument est un instantané figé à l'ouverture, jamais
  // resynchronisé automatiquement). Vidé à chaque ouverture d'un document
  // pour ne jamais laisser un rappel optimiste périmé s'afficher.
  const [pendingReminderEvents, setPendingReminderEvents] = useState([]);

  // const [deleting, setDeleting] = useState({});

  const downloadDocument = (envelopeId) => {
    const doc = documents.find((d) => d.envelopeId === envelopeId);
    setDownloading((prev) => ({ ...prev, [envelopeId]: true }));

    httpClient
      .post(
        "https://backend-myalfa.vercel.app/api/envelope/download",
        { envelopeId },
        { responseType: "blob" },
      )
      .then((response) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" }),
        );

        const formattedDate = new Date(doc.createdAt)
          .toLocaleDateString("fr-FR")
          .replace(/\//g, "-");
        const fileName = `${doc.emailSubject} - ${formattedDate}.pdf`;

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch((error) => {
        console.error("Erreur lors du téléchargement", error);
      })
      .finally(() => {
        setDownloading((prev) => ({ ...prev, [envelopeId]: false }));
      });
  };

  const processAndSendEmail = async (envelopeId, signer, emailSubject) => {
    const processKey = `${envelopeId}_${signer.id}`;
    setProcessing((prev) => ({ ...prev, [processKey]: true }));

    const toast = showStatusToast({
      status: "loading",
      titre: "Envoi du rappel...",
      message: `Envoi en cours à ${signer.email}`,
    });

    try {
      if (!signer.recipientId) {
        toast.update({
          status: "error",
          titre: "Rappel non envoyé",
          message: "Erreur : recipientId manquant pour ce signataire.",
        });
        return;
      }

      // Lien statique : redirige vers DocuSign à la volée à chaque clic,
      // contrairement à /api/remind qui renvoyait une URL DocuSign à usage unique.
      const url = `https://backend-myalfa.vercel.app/api/signing-link/${envelopeId}/${signer.recipientId}`;

      await httpClient.post(
        "https://backend-myalfa.vercel.app/api/send-url-email",
        {
          email: signer.email,
          url,
          subject: emailSubject,
          envelopeId,
        },
      );

      toast.update({
        status: "success",
        titre: "Rappel envoyé",
        message: `Email envoyé à ${signer.email}`,
      });

      // Affichage instantané dans la Timeline, en attendant que le vrai
      // historique (créé côté backend) revienne via un refetch — cet objet
      // n'est jamais persisté, juste un aperçu optimiste local.
      const auteurNom = [utilisateur?.name, utilisateur?.lastName]
        .filter(Boolean)
        .join(" ");
      setPendingReminderEvents((prev) => [
        ...prev,
        {
          createdAt: new Date().toISOString(),
          titre: "Rappel manuel de signature envoyé",
          description: auteurNom
            ? `Rappel manuel envoyé à ${signer.email} par ${auteurNom}`
            : `Rappel manuel envoyé à ${signer.email}`,
        },
      ]);

      fetchDocumentsLM();
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'email", error);
      toast.update({
        status: "error",
        titre: "Échec de l'envoi",
        message: "Une erreur est survenue lors de l'envoi du rappel.",
      });
    } finally {
      setProcessing((prev) => ({ ...prev, [processKey]: false }));
    }
  };

  const handleArchive = async (doc) => {
    const commentaire = window.prompt(
      "Voulez-vous vraiment archiver ce lead ?\n\nVous pouvez saisir un commentaire ci-dessous :",
    );

    if (commentaire === null) return;

    const payload = {
      specificite: "archive",
      lastspecificite: doc.specificite,
      commentaire, // Inclure le texte entré ici
    };

    // UI instantanée : la carte part aux archives tout de suite,
    // l'appel API se fait en arrière-plan
    setShowOverlay(false);
    setDocuments((prev) =>
      prev.map((d) => (d.id === doc.id ? { ...d, ...payload } : d)),
    );

    try {
      const response = await authFetch(
        `https://backend-myalfa.vercel.app/api/update-envelope/${doc.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error("Erreur lors de l’archivage.");
      }

      fetchDocumentsLM();
    } catch (error) {
      console.error("Erreur d’archivage :", error);
      // Échec : la carte revient dans sa colonne d'origine
      setDocuments((prev) => prev.map((d) => (d.id === doc.id ? doc : d)));
      dialog.alert("Échec de l’archivage.");
    }
  };

  const effectiveDocuments = docOverrides
    ? documents.map((d) =>
        docOverrides[d.id] ? { ...d, ...docOverrides[d.id] } : d,
      )
    : documents;

  const filteredDocuments = effectiveDocuments
    .filter(
      (doc) =>
        doc.specificite !== "completed" &&
        doc.specificite !== "archive" &&
        doc.specificite !== "archive_auto" &&
        doc.status !== "completed",
    )
    .filter((doc) =>
      `${doc.emailSubject}`.toLowerCase().includes(searchTerm.toLowerCase()),
    );

  const deleteDocument = async (id) => {
    if (!(await dialog.confirm("Supprimer ce document ?"))) return;

    // UI instantanée : la carte disparaît tout de suite,
    // la suppression réelle se fait en arrière-plan
    const docToDelete = documents.find((d) => d.id === id);
    setShowOverlay(false);
    setDocuments((prev) => prev.filter((d) => d.id !== id));

    try {
      await httpClient.delete(
        `https://backend-myalfa.vercel.app/api/delete-envelope/${id}`,
      );
      fetchDocumentsLM();
    } catch (error) {
      console.error("Erreur suppression", error);
      // Échec : on remet la carte
      if (docToDelete) setDocuments((prev) => [...prev, docToDelete]);
      dialog.alert("Erreur lors de la suppression.");
    }
  };

  const civilite = selectedDocument?.formLead?.civilite;
  const prenom = selectedDocument?.formLead?.prenom;
  const nom = selectedDocument?.formLead?.nom;

  const isEmptyLeadDetails = [civilite, prenom, nom].every(
    (v) => !v || (typeof v === "string" && v.trim() === ""),
  );

  const daysDiff = selectedDocument
    ? Math.floor(
        (new Date() - new Date(selectedDocument.createdAt)) /
          (1000 * 60 * 60 * 24),
      )
    : null;

  const getDaysDiff = (date) => {
    const today = new Date();
    const created = new Date(date);

    // On enlève l'heure pour comparer uniquement les jours
    today.setHours(0, 0, 0, 0);
    created.setHours(0, 0, 0, 0);

    return Math.floor((today - created) / (1000 * 60 * 60 * 24));
  };

  return (
    <div>
      <div className="relative flex justify-center mb-5">
        <h2 className="relative inline-flex w-[88%] items-center justify-center rounded-2xl border border-[#6e1a36] text-[#6e1a36] bg-[#f3edef] px-6 py-2 text-sm font-semibold tracking-wide shadow-md transition-all duration-300 hover:shadow-lg hover:scale-[1.02]">
          Proposition LM
          {/* Badge compteur */}
          <span className="absolute -top-2 -right-3 flex h-6 min-w-[24px] items-center justify-center rounded-full bg-[#6e1a36] px-2 text-[11px] font-bold text-white shadow-md">
            {filteredDocuments.length > 0 && `${filteredDocuments.length}`}
          </span>
        </h2>
      </div>

      {isLoading ? (
        <div>
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="relative mx-[4%] mb-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-md"
            >
              <div className="absolute top-5 right-3 h-4 w-14 rounded-full bg-slate-200 animate-pulse" />
              <div className="h-3 rounded bg-slate-200 animate-pulse w-32 mb-1.5" />
              <div className="h-2.5 rounded bg-slate-200 animate-pulse w-40 mb-3" />
              <div className="flex items-center justify-between mt-1">
                <div className="h-2.5 rounded bg-slate-200 animate-pulse w-24" />
                <div className="h-6 w-6 rounded-md bg-slate-200 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Droppable droppableId={columnId} isDropDisabled={isLoading}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`min-h-[60px] rounded-xl transition-colors ${snapshot.isDraggingOver ? "bg-[#840040]/5" : ""}`}
            >
              {filteredDocuments.length === 0 ? (
                <p style={{ textAlign: "center" }}>Aucun lead trouvé.</p>
              ) : (
                filteredDocuments
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .map((doc, index) => (
                    <Draggable
                      key={String(doc.id)}
                      draggableId={String(doc.id)}
                      index={index}
                    >
                      {(draggableProvided, draggableSnapshot) => (
                        <div
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                          onClick={() => {
                            setSelectedDocument(doc);
                            setPendingReminderEvents([]);
                            setShowOverlay(true);
                          }}
                          className={`relative mx-[4%] mb-3 cursor-pointer rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-[#840040] hover:from-white hover:to-[#840040/5] ${
                            draggableSnapshot.isDragging
                              ? "shadow-2xl border-[#840040] scale-[1.02] opacity-90 rotate-1"
                              : ""
                          }`}
                        >
                          {loadingDocIds?.has(doc.id) && (
                            <div className="absolute inset-0 bg-white/80 rounded-2xl flex items-center justify-center z-10">
                              <div className="w-5 h-5 border-2 border-[#840040] border-t-transparent rounded-full animate-spin" />
                            </div>
                          )}
                          <span
                            className={`absolute top-5 right-3 px-2 py-[1px] rounded-full text-[7px] font-medium border ${
                              getDaysDiff(doc.createdAt) === 7
                                ? "bg-yellow-50 text-red-800 border-red-300"
                                : getStatusBadge(doc.status)
                            }`}
                          >
                            {getDaysDiff(doc.createdAt) === 7
                              ? "Relancer"
                              : traduireStatut(doc.status)}
                          </span>
                          {/* Sujet email en deux lignes */}
                          <div className="flex flex-col w-full">
                            <p className="text-xs font-bold text-gray-800">
                              {(() => {
                                const name = doc.emailSubject
                                  ?.split(" - ")[0]
                                  ?.trim();

                                const finalName =
                                  !name || name.toLowerCase() === "n/a"
                                    ? `${doc.nom || "-"} ${doc.prenom || "-"}`
                                    : name;

                                return finalName.length > 17
                                  ? finalName.slice(0, 17) + "..."
                                  : finalName;
                              })()}
                            </p>

                            {decouperSujetLM(doc.emailSubject).complement && (
                              <p className="text-[11px] font-medium text-gray-500">
                                {tronquer(decouperSujetLM(doc.emailSubject).complement, 25)}
                              </p>
                            )}
                            <p className="text-[11px] font-medium text-gray-400">
                              {tronquer(decouperSujetLM(doc.emailSubject).type, 25)}
                            </p>
                          </div>

                          {/* Infos date et bouton */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div style={{ fontSize: "10px", color: "#555" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "start",
                                  flexDirection: "column",
                                }}
                              >
                                <p className="text-[10px] text-gray-500 font-normal">
                                  Créer le{" "}
                                  {new Date(doc.createdAt).toLocaleDateString(
                                    "fr-FR",
                                    {
                                      day: "numeric",
                                      month: "long",
                                      year: "numeric",
                                    },
                                  )}
                                </p>
                              </div>
                            </div>

                            <div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadDocument(doc.envelopeId);
                                }}
                                disabled={downloading[doc.envelopeId]}
                              >
                                {downloading[doc.envelopeId] ? (
                                  <div className="spinnerd"></div>
                                ) : (
                                  <MdDownload size={15} color="#840040" />
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Overlay pour Signers */}
                        </div>
                      )}
                    </Draggable>
                  ))
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      )}
      {showOverlay && selectedDocument && (
        <div className="overlayAC" ref={overlayRef}>
          <div className="inoverlayLead" ref={inoverlayRef}>
            <button
              className="close-button"
              onClick={(e) => {
                e.stopPropagation(); // ← Empêche le click de remonter
                setShowOverlay(false); // ← Ferme l'overlay
              }}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "transparent",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                zIndex: 10000,
              }}
            >
              <button
                aria-label="Fermer"
                className="p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </button>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                padding: "0 5%",
                boxSizing: "border-box",
              }}
            >
              <div className="flex  justify-between mb-6 items-center">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#9B1B5D] to-[#7A1548] flex items-center justify-center">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <div className="text-1xl font-bold text-slate-900 m-0">
                      {(() => {
                        const name = selectedDocument.emailSubject
                          ?.split(" - ")[0]
                          ?.trim();
                        return !name || name.toLowerCase() === "n/a"
                          ? `${selectedDocument.nom || "-"} ${
                              selectedDocument.prenom || "-"
                            }`
                          : name;
                      })()}{" "}
                    </div>
                    {decouperSujetLM(selectedDocument.emailSubject).complement && (
                      <div className="text-1xl font-semibold text-slate-500 m-0">
                        {decouperSujetLM(selectedDocument.emailSubject).complement}
                      </div>
                    )}
                    <div className="text-1xl font-bold text-slate-400 m-0">
                      {decouperSujetLM(selectedDocument.emailSubject).type}
                    </div>

                    <span
                      className={`px-2 py-[1px] rounded-full text-[7px] font-medium border ${
                        daysDiff === 7
                          ? "bg-yellow-50 text-red-800 border-red-300"
                          : getStatusBadge(selectedDocument.status)
                      }`}
                    >
                      {daysDiff === 7
                        ? "Relancer"
                        : traduireStatut(selectedDocument.status)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 mr-10">
                  {/* Badge du cabinet signataire de la LM */}
                  {selectedDocument.cabinet && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#9B1B5D]/10 text-[#9B1B5D] border border-[#9B1B5D]/20">
                      <Building2 className="w-3.5 h-3.5" />
                      {selectedDocument.cabinet}
                    </span>
                  )}

                  <div className="relative">
                    <button
                      onClick={() => setShowMenu(!showMenu)}
                      className="p-2 rounded-md hover:bg-gray-100"
                    >
                      <MoreVertical className="w-5 h-5 text-gray-600" />
                    </button>

                    {showMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                        <div
                          className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            handleArchive(selectedDocument);
                            setShowMenu(false);
                          }}
                        >
                          Archiver
                        </div>

                        <div
                          className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            deleteDocument(selectedDocument.id);
                            setShowMenu(false);
                          }}
                        >
                          Supprimer
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid w-full grid-cols-2 bg-slate-100 p-1 h-auto rounded-md mb-6">
                {[
                  { value: "general", label: "Général" },

                  { value: "timeline", label: "Timeline" },
                ].map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => setActiveTab(tab.value)}
                    className={`py-2 text-xs rounded-md transition-colors font-medium
              ${
                activeTab === tab.value
                  ? "bg-white"
                  : "bg-transparent hover:bg-slate-200"
              }
            `}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div>
                {activeTab === "general" && (
                  <>
                    <div className="details-view" style={{ width: "100%" }}>
                      {isEmptyLeadDetails ? (
                        <p
                          style={{
                            color: "gray",
                            fontStyle: "italic",
                            marginTop: "10px",
                          }}
                        ></p>
                      ) : (
                        <>
                          <div className="bg-white rounded-lg p-4 border border-slate-200 mb-5">
                            <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                              <Briefcase className="w-4 h-4 text-[#9B1B5D]" />
                              Information de Rendez-vous
                            </h3>
                            <div className="space-y-3 text-sm">
                              <div className="items-start justify-between">
                                <span className="text-slate-500">
                                  Objet du Rendez-vous :{" "}
                                </span>
                                <span className="font-medium text-slate-900 text-right">
                                  {selectedDocument?.formLead?.objetRDV}
                                </span>
                              </div>

                              {selectedDocument?.formLead?.user && (
                                <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
                                  <div className="flex items-center gap-2 text-xs text-slate-600">
                                    <Calendar className="w-4 h-4 text-[#9B1B5D]" />
                                    <span>
                                      Le{" "}
                                      {new Date(
                                        selectedDocument?.formLead?.dateRDV,
                                      ).toLocaleDateString("fr-FR", {
                                        day: "numeric",
                                        month: "long",
                                        year: "numeric",
                                      })}{" "}
                                      à{" "}
                                      {
                                        selectedDocument?.formLead?.heureRDV
                                      }{" "}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-2 text-xs text-slate-600">
                                    <MapPin className="w-4 h-4 text-[#9B1B5D]" />
                                    <span>
                                      {" "}
                                      {selectedDocument?.formLead?.lieuRDV}{" "}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-2 text-xs text-slate-600">
                                    <User className="w-4 h-4 text-[#9B1B5D]" />
                                    <span>
                                      {selectedDocument?.formLead?.user.name}{" "}
                                      {
                                        selectedDocument?.formLead?.user
                                          .lastName
                                      }
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Information client */}
                        <div className="bg-white rounded-lg p-4 border border-slate-200">
                          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-[#9B1B5D]" />
                            Information client
                          </h3>
                          <div className="space-y-2">
                            {/* Infos générales */}
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Civilité</span>
                              <span className="font-medium text-slate-900">
                                {selectedDocument.civilite_lm || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Nom</span>
                              <span className="font-medium text-slate-900">
                                {selectedDocument.nom || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Prenom</span>
                              <span className="font-medium text-slate-900">
                                {selectedDocument.prenom || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Adresse</span>
                              <span className="font-medium text-slate-900">
                                {selectedDocument.adresse || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Telephone</span>
                              <span className="font-medium text-slate-900">
                                {selectedDocument.telephone || "-"}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-500">Email</span>
                              <span className="font-medium text-slate-900">
                                {selectedDocument.email || "-"}
                              </span>
                            </div>

                            {/* Contact représentant */}
                            <div className="bg-slate-50 rounded-lg p-2 mt-2 space-y-1.5">
                              <p className="text-xs font-semibold text-slate-700 mb-1">
                                Structure existante
                              </p>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-500">
                                  Forme Social
                                </span>
                                <span className="font-medium text-slate-900">
                                  {selectedDocument.formeSociale || "-"}{" "}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-500">
                                  Dénomination Social
                                </span>
                                <span className="font-medium text-slate-900">
                                  {selectedDocument.societeNom || "-"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Équipe Cabinet */}
                        <div className="bg-white rounded-lg p-4 border border-slate-200">
                          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <Users className="w-4 h-4 text-[#9B1B5D]" />
                            Équipe Cabinet
                          </h3>

                          <div className="space-y-2.5">
                            {/* Expert comptable */}
                            <div className="flex items-center gap-2 p-2 bg-[#9B1B5D]/5 rounded-lg">
                              <div className="w-8 h-8 rounded-full bg-[#9B1B5D] flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-slate-500">
                                  Signataire
                                </p>
                                <p className="text-xs font-semibold text-slate-900 truncate">
                                  {(() => {
                                    const signataire = getSignataireComptable(
                                      selectedDocument?.signers,
                                    );
                                    return signataire
                                      ? COMPTABLE_SIGNATAIRES[
                                          signataire.email.toLowerCase()
                                        ]
                                      : "—";
                                  })()}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-5">
                        <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#9B1B5D]" />
                          Documents liés
                        </h3>
                        <LeadDocumentsList lead={selectedDocument?.formLead} />
                      </div>
                      <div className="space-y-3 mt-5">
                        {selectedDocument.signers.map((signer) => {
                          const processKey = `${selectedDocument.envelopeId}_${signer.id}`;
                          return (
                            <div
                              key={signer.id}
                              className="bg-white border border-slate-200 rounded-lg p-3 flex items-center justify-between"
                            >
                              {/* Info signer */}
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-[#9B1B5D]/10 flex items-center justify-center flex-shrink-0">
                                  <User className="w-4 h-4 text-[#9B1B5D]" />
                                </div>
                                <div className="flex flex-col text-xs">
                                  <span className="font-medium text-slate-900 truncate">
                                    {signer.name}
                                  </span>
                                </div>
                                <div className="flex flex-col text-xs ml-4">
                                  <span className="font-medium text-slate-900 truncate">
                                    {signer.email}
                                  </span>
                                </div>
                              </div>

                              {/* Bouton rappel */}
                              <button
                                onClick={() =>
                                  processAndSendEmail(
                                    selectedDocument.envelopeId,
                                    signer,
                                    selectedDocument.emailSubject,
                                  )
                                }
                                disabled={processing[processKey]}
                                className={`text-xs font-medium px-3 py-1 rounded-sm ${
                                  processing[processKey]
                                    ? "bg-[#9B1B5D]/50 cursor-not-allowed"
                                    : "bg-[#9B1B5D] hover:bg-[#840040]"
                                } text-white`}
                              >
                                {processing[processKey] ? (
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  "Renvoyer un Mail de rappel"
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
                {activeTab === "timeline" && (
                  <div className="bg-white border border-slate-200 rounded-lg p-4 mt-0">
                    {/* Header */}
                    <div className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-900 mb-4">
                      <div className="w-2 h-2 rounded-full bg-[#8B1538]"></div>
                      Timeline
                    </div>

                    {/* Tri des événements du plus récent au moins récent */}
                    {[
                      {
                        date: selectedDocument?.formLead?.createdAt,
                        titre: "Création de la fiche LEAD",
                        description: `Création de la fiche LEAD avec ${selectedDocument?.formLead?.specificite}`,
                        data: null,
                      },
                      {
                        date: selectedDocument?.createdAt,
                        titre: "Création du LM",
                        description: null,
                        data: null,
                      },
                      ...(
                        selectedDocument?.formLead?.HistoriqueFormLEAD || []
                      ).map((item) => ({
                        date: item.createdAt,
                        titre: item.titre,
                        description: item.description,
                        data: item,
                      })),
                      ...pendingReminderEvents.map((item) => ({
                        date: item.createdAt,
                        titre: item.titre,
                        description: item.description,
                        data: null,
                      })),
                    ]
                      .filter((event) => event.date)
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((event, index) => {
                        // Rappels de signature (auto = relance J-7 système,
                        // manuel = bouton "Renvoyer un Mail de rappel") :
                        // distingués visuellement du reste de la timeline.
                        const isRappel = event.titre?.startsWith("Rappel");
                        const isRappelAuto = event.titre?.includes(
                          "automatique",
                        );
                        const pointColor = isRappel
                          ? isRappelAuto
                            ? "#f59e0b"
                            : "#ea580c"
                          : "#2563eb";
                        const cardBg = isRappel ? "#fff7ed" : "#eff6ff";
                        const cardBorder = isRappel ? "#fed7aa" : "#cbd5e1";
                        const titleColor = isRappel ? "#9a3412" : "#1e40af";
                        const descColor = isRappel ? "#7c2d12" : "#1e3a8a";

                        return (
                        <div
                          key={index}
                          style={{
                            position: "relative",
                            paddingLeft: "20px",
                            marginBottom: "15px",
                            borderLeft: "2px solid #cbd5e1",
                          }}
                        >
                          {/* Point timeline */}
                          <div
                            style={{
                              position: "absolute",
                              left: "-6px",
                              top: "6px",
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              backgroundColor: pointColor,
                              border: "2px solid #fff",
                            }}
                          ></div>

                          {/* Card */}
                          <div
                            style={{
                              background: cardBg,
                              borderRadius: "8px",
                              padding: "10px",
                              border: `1px solid ${cardBorder}`,
                            }}
                          >
                            <div className="flex justify-between items-start mb-1">
                              <p
                                className="text-[13px] font-semibold"
                                style={{ color: titleColor }}
                              >
                                {isRappel && "🔔 "}
                                {event.titre}
                                {isRappel && (
                                  <span
                                    className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                                    style={{
                                      background: isRappelAuto
                                        ? "#fef3c7"
                                        : "#ffedd5",
                                      color: isRappelAuto
                                        ? "#92400e"
                                        : "#9a3412",
                                    }}
                                  >
                                    {isRappelAuto ? "Système" : "Manuel"}
                                  </span>
                                )}
                              </p>
                              <span className="text-xs" style={{ color: titleColor }}>
                                {new Date(event.date).toLocaleDateString(
                                  "fr-FR",
                                  {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                  },
                                )}
                              </span>
                            </div>

                            {event.description && (
                              <p
                                className="text-[13px]"
                                style={{ color: descColor }}
                              >
                                {event.description}
                              </p>
                            )}

                            {/* Détails formulaire si nécessaire */}
                            {event.data &&
                              event.data.titre ===
                                "Formulaire de Rendez-vous renseigné" && (
                                <>
                                  <span
                                    onClick={() =>
                                      setOpenDetails((prev) => ({
                                        ...prev,
                                        [event.data.id]: !prev[event.data.id],
                                      }))
                                    }
                                    style={{
                                      color: "#1e40af",
                                      cursor: "pointer",
                                      fontSize: "12px",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    {openDetails[event.data.id]
                                      ? "masquer détails"
                                      : "voir détails"}
                                  </span>

                                  {openDetails[event.data.id] && (
                                    <div
                                      style={{
                                        marginTop: "10px",
                                        background: "#ffffff",
                                        borderRadius: "8px",
                                        padding: "10px",
                                        border: "1px solid #cbd5e1",
                                      }}
                                    >
                                      <h4 className="text-sm font-semibold text-blue-800 mb-3">
                                        Détails Formulaire rempli
                                      </h4>

                                      <table
                                        style={{
                                          width: "100%",
                                          borderCollapse: "collapse",
                                        }}
                                      >
                                        <tbody>
                                          {[
                                            [
                                              "Nom complet",
                                              event.data.prospect_nomComplet,
                                            ],
                                            [
                                              "Email",
                                              event.data.prospect_email,
                                            ],
                                            [
                                              "Téléphone",
                                              event.data.prospect_telephone,
                                            ],
                                            [
                                              "Adresse",
                                              event.data.prospect_adresse,
                                            ],
                                            [
                                              "Type de prospect",
                                              event.data.prospect_prospectType,
                                            ],
                                            [
                                              "Description du projet",
                                              event.data
                                                .prospect_descriptionProjet,
                                            ],
                                            [
                                              "Nombre d’associés",
                                              event.data
                                                .prospect_nombreAssocies,
                                            ],
                                            [
                                              "Forme sociale",
                                              event.data.prospect_formSociale,
                                            ],
                                            [
                                              "Numéro SIRET",
                                              event.data.prospect_numSiret,
                                            ],
                                            [
                                              "Nom de la société",
                                              event.data.prospect_nomSociete,
                                            ],
                                            [
                                              "Adresse société",
                                              event.data
                                                .prospect_adresseSociete,
                                            ],
                                            [
                                              "Date de création société",
                                              event.data
                                                .prospect_dateCreationSociete,
                                            ],
                                            [
                                              "Description du besoin",
                                              event.data
                                                .prospect_descriptionBesoin,
                                            ],
                                            [
                                              "Service sélectionné",
                                              event.data
                                                .prospect_selectedService,
                                            ],
                                          ].map(([label, value]) => (
                                            <tr key={label}>
                                              <td
                                                style={{
                                                  fontWeight: "600",
                                                  padding: "5px 10px",
                                                  width: "35%",
                                                  color: "#1e3a8a",
                                                }}
                                              >
                                                {label} :
                                              </td>
                                              <td
                                                style={{
                                                  padding: "5px 10px",
                                                  color: "#1e3a8a",
                                                }}
                                              >
                                                {value ?? " "}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </>
                              )}
                          </div>
                        </div>
                        );
                      })}
                  </div>
                )}
              </div>
              <br />

              <br />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PropLM;
